/**
 * Modern sort dropdown that drives the hidden native `select[name="sort_by"]`.
 * Picking an option writes the select value and dispatches `change`, so the
 * surrounding s-live-form refreshes results exactly as before.
 */
class SortDropdown extends HTMLElement {
  #ac;
  #outsideAc;
  #closingAnimation;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#button?.addEventListener('click', () => this.#toggle(), opts);

    this.#panel?.addEventListener('click', (event) => {
      const option = event.target instanceof Element ? event.target.closest('[data-sort-option]') : null;
      if (!option) return;
      this.#choose(option.getAttribute('data-value') ?? '');
    }, opts);

    this.addEventListener('keydown', (event) => this.#onKeydown(event), opts);

    // Keep the trigger in sync when history restore rewrites the select value.
    this.#select?.addEventListener('change', () => this.#syncFromSelect(), opts);
    this.#syncFromSelect();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#removeOutsideListeners();
    this.#closingAnimation?.cancel();
  }

  get #button() {
    return this.querySelector('[data-sort-trigger]');
  }

  get #panel() {
    return this.querySelector('[data-sort-panel]');
  }

  get #select() {
    return this.querySelector('select[name="sort_by"]');
  }

  get #options() {
    return Array.from(this.querySelectorAll('[data-sort-option]'));
  }

  get #isHidden() {
    return this.#panel?.hasAttribute('hidden') ?? true;
  }

  #toggle() {
    if (this.#isHidden) this.#show();
    else void this.#hide();
  }

  #choose(value) {
    const select = this.#select;
    void this.#hide();
    this.#button?.focus();
    if (!select || select.value === value) return;
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #syncFromSelect() {
    const select = this.#select;
    if (!select) return;
    const label = this.querySelector('[data-sort-current]');
    const current = select.options[select.selectedIndex];
    if (label && current) label.textContent = current.textContent;
    for (const option of this.#options) {
      const selected = option.getAttribute('data-value') === select.value;
      option.setAttribute('aria-selected', String(selected));
      option.querySelector('[data-sort-check]')?.classList.toggle('invisible', !selected);
    }
  }

  #onKeydown(event) {
    if (event.key === 'Escape' && !this.#isHidden) {
      event.stopPropagation();
      void this.#hide();
      this.#button?.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();

    if (this.#isHidden) {
      this.#show();
      return;
    }

    const options = this.#options;
    const index = options.indexOf(document.activeElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const next = options[(index + step + options.length) % options.length];
    next?.focus();
  }

  #show() {
    const panel = this.#panel;
    const button = this.#button;
    if (!panel || !button || !this.#isHidden) return;

    this.#closingAnimation?.cancel();
    this.#closingAnimation = null;
    panel.removeAttribute('hidden');
    button.setAttribute('aria-expanded', 'true');
    this.#addOutsideListeners();

    const selected = this.#options.find((option) => option.getAttribute('aria-selected') === 'true');
    (selected ?? this.#options[0])?.focus({ preventScroll: true });
  }

  async #hide() {
    const panel = this.#panel;
    const button = this.#button;
    if (!panel || !button || this.#isHidden) return;

    this.#removeOutsideListeners();
    button.setAttribute('aria-expanded', 'false');

    if (this.#prefersReducedMotion() || typeof panel.animate !== 'function') {
      panel.setAttribute('hidden', '');
      return;
    }

    let animation;
    try {
      animation = panel.animate(
        [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, transform: 'translateY(-6px)' },
        ],
        { duration: 150, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    } catch {
      panel.setAttribute('hidden', '');
      return;
    }

    this.#closingAnimation = animation;
    try {
      await animation.finished;
    } catch {
      return;
    }
    if (this.#closingAnimation !== animation) return;
    this.#closingAnimation = null;
    panel.setAttribute('hidden', '');
  }

  #addOutsideListeners() {
    this.#removeOutsideListeners();
    this.#outsideAc = new AbortController();
    const opts = { capture: true, signal: this.#outsideAc.signal };
    document.addEventListener('pointerdown', this.#onOutsideInteraction, opts);
    document.addEventListener('focusin', this.#onOutsideInteraction, opts);
  }

  #onOutsideInteraction = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    void this.#hide();
  };

  #removeOutsideListeners() {
    this.#outsideAc?.abort();
    this.#outsideAc = null;
  }

  #prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('s-sort-dropdown')) {
  customElements.define('s-sort-dropdown', SortDropdown);
}
